import _ from 'underscore';
import React from 'react';
import { Activity } from '@bingads-webui-react/hoc-with-signal';
import { getNeedConfirming } from '@bingads-webui-campaign-react/campaign-settings-utils';

import {
  CampaignSettingsStep,
  TargetStep,
  BudgetBidStep,
  SummaryStep,
  useCampaignRepository,
  useBudgetBidRepository,
  useSummaryRepository,
  useTargetRepository,
} from './index';
import { STEP_PATHS, CREATE_SCENARIO } from '../constants';
import { AdGroupSettings } from './adgroup-settings';

export const getStepConfig = ({ flowType, createScenario, permissions = {} }) => {
  if (createScenario === CREATE_SCENARIO.CREATE_CAMPAIGN) {
    return [{
      id: STEP_PATHS.CAMPAIGN_SETTINGS,
      view: props => <Activity name="CampaignSettings"><CampaignSettingsStep {...props} /></Activity>,
      repository: useCampaignRepository,
      viewProps: { flowType },
    }, {
      id: STEP_PATHS.TARGET_SETTINGS,
      view: props => <Activity name="TargetSettings"><TargetStep {...props} /></Activity>,
      repository: useTargetRepository,
      viewProps: { flowType },
    }, {
      id: STEP_PATHS.BUDGET_AND_BIDS,
      view: props => <Activity name="BudgetAndBidsSetting"><BudgetBidStep {...props} /></Activity>,
      repository: useBudgetBidRepository,
      viewProps: { flowType },
    },
    {
      id: STEP_PATHS.SUMMARY,
      view: props => <Activity name="Summary"><SummaryStep {...props} /></Activity>,
      repository: useSummaryRepository,
    }];
  }

  if (createScenario === CREATE_SCENARIO.UPDATE_CAMPAIGN) {
    const stepConfig = {
      id: STEP_PATHS.CAMPAIGN_SETTINGS,
      view: props => <Activity name="CampaignSettings"><CampaignSettingsStep {...props} /></Activity>,
      repository: useCampaignRepository,
      viewProps: { flowType, createScenario },
    };

    if (_.get(permissions, ['dynamic', 'ConversionAssociationWarningP3'], false)) {
      stepConfig.machineOptions = {
        guards: {
          needsConfirming: context => getNeedConfirming(permissions, context),
        },
      };
    }

    return [stepConfig];
  }

  return [{
    id: STEP_PATHS.AD_GROUP_SETTINGS,
    view: props => <Activity name="AdGroupSettings"><AdGroupSettings {...props} /></Activity>,
    repository: useTargetRepository,
    viewProps: { flowType, createScenario },
  }];
};

// for now. AWF and SWF share the same step list. if they are different, we can return different step list based on the flow
export const getStepListConfig = ({ i18n, createScenario }) => {
  if (createScenario === CREATE_SCENARIO.CREATE_CAMPAIGN) {
    return [{
      id: STEP_PATHS.CAMPAIGN_SETTINGS,
      title: i18n.getString(_TL_('Shopping settings')),
    },
    {
      id: STEP_PATHS.TARGET_SETTINGS,
      title: i18n.getString(_TL_('Targeting')),
    },
    {
      id: STEP_PATHS.BUDGET_AND_BIDS,
      title: i18n.getString(_TL_('Budget & bids')),
    },
    {
      id: STEP_PATHS.SUMMARY,
      title: i18n.getString(_TL_('Summary')),
    }];
  }

  if (createScenario === CREATE_SCENARIO.UPDATE_CAMPAIGN) {
    return [{
      id: STEP_PATHS.CAMPAIGN_SETTINGS,
      title: i18n.getString(_TL_('Campaign Settings')),
    }];
  }

  return [{
    id: STEP_PATHS.AD_GROUP_SETTINGS,
    title: i18n.getString(_TL_('Ad Group Settings')),
  }];
};

export const getFlow = ({ createScenario }) => {
  if (createScenario === CREATE_SCENARIO.CREATE_CAMPAIGN) {
    return [
      STEP_PATHS.CAMPAIGN_SETTINGS,
      STEP_PATHS.TARGET_SETTINGS,
      STEP_PATHS.BUDGET_AND_BIDS,
      STEP_PATHS.SUMMARY,
    ];
  }

  if (createScenario === CREATE_SCENARIO.UPDATE_CAMPAIGN) {
    return [STEP_PATHS.CAMPAIGN_SETTINGS];
  }

  return [STEP_PATHS.AD_GROUP_SETTINGS];
};

export const getFirstStep = ({ createScenario }) => {
  switch (createScenario) {
    case CREATE_SCENARIO.CREATE_CAMPAIGN:
    case CREATE_SCENARIO.UPDATE_CAMPAIGN:
      return STEP_PATHS.CAMPAIGN_SETTINGS;

    case CREATE_SCENARIO.UPDATE_ADGROUP:
      return STEP_PATHS.AD_GROUP_SETTINGS;

    default:
      throw new Error('Unexpected scenario!');
  }
};
export const getDestinationWhenDone = ({ createScenario }) => {
  switch (createScenario) {
    case CREATE_SCENARIO.CREATE_CAMPAIGN:
      return 'campaigns';
    case CREATE_SCENARIO.UPDATE_ADGROUP:
      return 'ads';

    default:
      return 'adgroups';
  }
};
